import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Camera, X, ScanLine } from "lucide-react";
import { toast } from "sonner";

export default function CameraScanner({ open, onClose, onResult }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [scanning, setScanning] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    let stream;
    const start = async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "environment", width: { ideal: 1280 }, height: { ideal: 720 } },
        });
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
        }
      } catch {
        toast.error("Accesso alla fotocamera negato o non disponibile.");
        onClose();
      }
    };
    start();
    return () => {
      if (stream) stream.getTracks().forEach((t) => t.stop());
    };
  }, [open, onClose]);

  const capture = async () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d").drawImage(video, 0, 0);
    setScanning(true);
    try {
      const Tesseract = (await import("tesseract.js")).default;
      const { data } = await Tesseract.recognize(canvas.toDataURL("image/png"), "eng", {
        tessedit_char_whitelist: "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789",
        tessedit_pageseg_mode: "7",
      });
      const raw = (data.text || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
      const match =
        raw.match(/[A-Z]{2}\d{3}[A-Z]{2}/) ||
        raw.match(/[A-Z]{2}\d{4}/) ||
        raw.match(/[A-Z0-9]{5,8}/);
      if (match) {
        onResult(match[0]);
        toast.success(`Targa rilevata: ${match[0]}`);
        onClose();
      } else {
        toast.error("Targa non riconosciuta. Avvicinati e riprova, o digitala manualmente.");
      }
    } catch {
      toast.error("Scansione non riuscita. Digita la targa manualmente.");
    } finally {
      setScanning(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-sm flex flex-col items-center justify-center p-4"
          data-testid="camera-scanner-modal"
        >
          <div className="relative w-full max-w-[480px] rounded-2xl overflow-hidden border border-[#ff007f]/40 shadow-[0_0_40px_rgba(255,0,127,0.3)]">
            <video ref={videoRef} playsInline muted className="w-full h-auto bg-black" data-testid="camera-video-preview" />
            <div className="absolute inset-x-8 top-1/2 -translate-y-1/2 h-24 rounded-lg border-2 border-[#00f0ff]/70 shadow-[0_0_25px_rgba(0,240,255,0.35)] pointer-events-none" />
            {scanning && <div className="absolute inset-x-0 h-1 bg-[#00f0ff] animate-scan" />}
            <button
              onClick={onClose}
              data-testid="camera-scanner-close-button"
              className="absolute top-3 right-3 w-9 h-9 rounded-full bg-black/70 border border-white/20 flex items-center justify-center text-white/90 hover:bg-black transition-colors cursor-pointer"
              aria-label="Chiudi fotocamera"
            >
              <X size={16} />
            </button>
          </div>
          <button
            onClick={capture}
            disabled={scanning}
            data-testid="camera-scanner-capture-button"
            className="mt-5 px-6 py-3.5 rounded-xl bg-[#ff007f] font-orbitron text-[11px] font-bold tracking-[0.2em] text-white shadow-[0_0_20px_rgba(255,0,127,0.4)] hover:shadow-[0_0_34px_rgba(255,0,127,0.6)] hover:-translate-y-0.5 transition-all flex items-center gap-2 cursor-pointer disabled:opacity-60"
          >
            {scanning ? (
              <>
                <ScanLine size={15} className="animate-pulse" /> OCR IN CORSO...
              </>
            ) : (
              <>
                <Camera size={15} /> ACQUISISCI TARGA
              </>
            )}
          </button>
          <p className="font-jetmono text-[10px] tracking-[0.2em] text-[#8a8a9e] mt-4 text-center">
            INQUADRA LA TARGA NEL RIQUADRO, POI ACQUISISCI
          </p>
          <canvas ref={canvasRef} className="hidden" />
        </motion.div>
      )}
    </AnimatePresence>
  );
}
